import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createFileTableColumns } from './my-files-table-columns';
import { useIsMobile } from 'hooks/use-mobile';
import { cn } from 'lib/utils';
import DataTable from 'components/blocks/data-table/data-table';
import {
  IFileDataWithSharing,
  MyFilesListViewProps,
  PaginationState,
} from '../../utils/file-manager';
import { RegularFileDetailsSheet } from '../regular-file-details-sheet';
import PDFViewer from '../pdf-viewer';
import { useFileManagerConfig } from '../../hooks/use-file-manager-config';
import { SortingState } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { useConfig } from 'hooks/use-config';
import { getFileBlobUrl } from 'services/storage-service';
import { useLibraryCurrentFolder } from '../../hooks/use-library-current-folder';

const MyFilesListView: React.FC<MyFilesListViewProps> = ({
  onViewDetails,
  onDownload,
  onShare,
  onSharedWithTenant,
  onDelete,
  onRename,
  onMove,
  filters,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { configurationSettings } = useConfig();

  const config = useFileManagerConfig();
  const isMediaLibrary = config.type === 'media';
  const extraParams = config.type === 'document' || config.type === 'shared'
    ? { sharedOnly: false }
    : undefined;

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<IFileDataWithSharing | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
    totalCount: 0,
  });

  const initialSortingState = config.type === 'document' ? [{ id: 'lastModified', desc: true }] : config.type === 'shared' ? [{ id: 'sharedDate', desc: true }] : [{ id: 'createdDate', desc: true }];

  const [sortingState, setSortingState] = useState<SortingState>(initialSortingState);

  const queryParams = {
    page: paginationState.pageIndex,
    pageSize: paginationState.pageSize,
    filter: filters,
    sort: sortingState,
  };

  const { data, isLoading, error } = config.queryHook(queryParams, extraParams);
  const { openFolder } = useLibraryCurrentFolder();

  const tableData = useMemo(() => {
    const files = data?.data || [];
    if (!isMediaLibrary) {
      return files;
    }

    // Folders on this page already come from POST /media-library (same pageSize as files).
    if (filters.libraryTab === 'folders') {
      return files.filter((file: IFileDataWithSharing) => file.fileType === 'Folder');
    }
    if (filters.libraryTab === 'files') {
      return files.filter((file: IFileDataWithSharing) => file.fileType !== 'Folder');
    }
    return files;
  }, [data?.data, filters.libraryTab, isMediaLibrary]);

  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPaginationState((prev) => ({
        ...prev,
        pageIndex: newPagination.pageIndex,
        pageSize: newPagination.pageSize,
      }));
    },
    []
  );

  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSortingState(newSorting);
  }, []);

  useEffect(() => {
    if (!data) {
      return;
    }
    const fileTotalCount = data.fileTotalCount ?? data.totalCount ?? 0;
    const foldersTotalCount = data.foldersTotalCount ?? 0;
    const nextTotal =
      !isMediaLibrary
        ? data.totalCount
        : filters.libraryTab === 'folders'
          ? foldersTotalCount
          : filters.libraryTab === 'files'
            ? fileTotalCount
            : Math.max(foldersTotalCount, fileTotalCount);
    if (nextTotal !== undefined) {
      setPaginationState((prev) => ({
        ...prev,
        totalCount: nextTotal,
      }));
    }
  }, [data, filters.libraryTab, isMediaLibrary]);

  useEffect(() => {
    setPaginationState((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [filters]);

  const handleViewDetailsWrapper = useCallback(
    (file: IFileDataWithSharing) => {
      if (isMediaLibrary && file.fileType === 'Folder') {
        openFolder(file.id);
        return;
      }
      if (isMediaLibrary) {
        return;
      }
      setSelectedFile(file);
      setIsDetailsOpen(true);
      onViewDetails(file);
    },
    [isMediaLibrary, onViewDetails, openFolder]
  );

  const handleCloseDetails = useCallback(() => {
    setIsDetailsOpen(false);
    setSelectedFile(null);
  }, []);

  const handleDownloadWrapper = useCallback((file: IFileDataWithSharing) => {
    // setSelectedFile(file);
    onDownload(file);
  }, [onDownload]);

  const handleShareWrapper = useCallback(
    (file: IFileDataWithSharing) => {
      setSelectedFile(file);
      if (!onShare) return;
      onShare(file);
    },
    [onShare]
  );

  const handleSharedWithTenantWrapper = useCallback(
    (file: IFileDataWithSharing) => {
      setSelectedFile(file);
      if (!onSharedWithTenant) return;
      onSharedWithTenant(file);
    },
    [onSharedWithTenant]
  );

  const handleDeleteWrapper = useCallback(
    async (file: IFileDataWithSharing) => {
      if (!onDelete) return;
      setSelectedFile(file);
      try {
        await onDelete(file);
        queryClient.invalidateQueries({ queryKey: ['document-files'] });
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    },
    [onDelete]
  );

  const handleRenameWrapper = useCallback(
    async (file: IFileDataWithSharing) => {
      if (!onRename) return;
      await onRename(file);
    },
    [onRename]
  );

  const columns = createFileTableColumns({
    onViewDetails: handleViewDetailsWrapper,
    onDownload: handleDownloadWrapper,
    ...(onShare ? { onShare: handleShareWrapper } : {}),
    ...(onSharedWithTenant ? { onSharedWithTenant: handleSharedWithTenantWrapper } : {}),
    ...(onDelete ? { onDelete: handleDeleteWrapper } : {}),
    ...(onRename ? { onRename: handleRenameWrapper } : {}),
    ...(onMove ? { onMove } : {}),
    t,
    isMediaLibrary,
  });

  if (error) {
    return <div className="p-4 text-error">{t('ERROR_LOADING_FILES')}</div>;
  }

  const shouldHideMainContent = isMobile && isDetailsOpen;

  async function getFileUrl(fileId: any): Promise<string | undefined> {
    return getFileBlobUrl(fileId.toString(), configurationSettings, undefined);
  }

  const onDoubleClick = async (fileId: IFileDataWithSharing) => {
    if (!fileId) return;
    const Url = await getFileUrl(fileId);
    if (!Url) return;
    setFileUrl(Url);
  };

  return (
    <div className={cn('relative flex w-full', !isMediaLibrary && 'h-full rounded-xl overflow-hidden')}>
      {!shouldHideMainContent && (
        <div
          className={cn(
            'flex w-full flex-col',
            !isMediaLibrary && 'h-full overflow-y-auto'
          )}>
          <div className={cn('flex w-full flex-col gap-6 md:gap-8', !isMediaLibrary && 'h-full', isMediaLibrary && 'pb-4')}>
            <DataTable
              data={tableData}
              columns={columns}
              onRowClick={
                isMediaLibrary
                  ? (file: IFileDataWithSharing) => {
                      if (file.fileType === 'Folder') {
                        openFolder(file.id);
                      }
                    }
                  : handleViewDetailsWrapper
              }
              isLoading={isLoading}
              pagination={{
                pageIndex: paginationState.pageIndex,
                pageSize: paginationState.pageSize,
                totalCount: paginationState.totalCount,
              }}
              onPaginationChange={handlePaginationChange}
              onSortingChange={handleSortingChange}
              manualPagination={true}
              mobileColumns={['name']}
              expandable={true}
              onDoubleClick={onDoubleClick}
            />
          </div>
          {fileUrl && (
            <PDFViewer url={fileUrl} setUrl={setFileUrl} fileType={selectedFile?.fileType} />
          )}
        </div>
      )}

      <RegularFileDetailsSheet
        isOpen={isDetailsOpen}
        onClose={handleCloseDetails}
        file={
          selectedFile
            ? {
              ...selectedFile,
              lastModified:
                typeof selectedFile.lastModified === 'string'
                  ? selectedFile.lastModified
                  : (selectedFile.lastModified?.toISOString?.() ?? ''),
              isShared: selectedFile.isShared ?? false,
            }
            : null
        }
        t={t}
      />
    </div>
  );
};

export default MyFilesListView;
